import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { pageNotificationsApi } from '@/services/api'
import Button from '@/components/ui/Button'
import Badge from '@/components/ui/Badge'
import Alert from '@/components/ui/Alert'
import Modal from '@/components/ui/Modal'
import ResponsiveTable from '@/components/ui/ResponsiveTable'
import { Plus, Edit2, Trash2, Eye, EyeOff, Info } from 'lucide-react'

const PAGE_ROUTES = [
  { value: 'all', label: 'All Pages (Global)' },
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'barters', label: 'Barter Marketplace' },
  { value: 'my-barters', label: 'My Barters' },
  { value: 'barter-groups', label: 'Barter Groups' },
  { value: 'messages', label: 'Messages' },
  { value: 'support', label: 'Support' },
  { value: 'reviews', label: 'Reviews' },
  { value: 'billing', label: 'Billing' },
  { value: 'withdrawals', label: 'Withdrawals' },
  { value: 'coins', label: 'Barter Coins' },
  { value: 'settings', label: 'Settings' },
  { value: 'profile', label: 'Profile' },
]

const NOTIFICATION_TYPES = [
  { value: 'info', label: 'Info', color: 'blue' },
  { value: 'warning', label: 'Warning', color: 'yellow' },
  { value: 'success', label: 'Success', color: 'green' },
  { value: 'error', label: 'Error', color: 'red' },
]

export default function PageNotifications() {
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [editingNotification, setEditingNotification] = useState<any>(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [formData, setFormData] = useState({
    page_route: 'all',
    message: '',
    type: 'info',
    is_active: true,
  })

  const { data } = useQuery({
    queryKey: ['page-notifications'],
    queryFn: () => pageNotificationsApi.list(),
  })

  const createMutation = useMutation({
    mutationFn: (data: any) => pageNotificationsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['page-notifications'] })
      setShowModal(false)
      setEditingNotification(null)
      resetForm()
      setSuccess('Notification created successfully!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to create notification')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      pageNotificationsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['page-notifications'] })
      setShowModal(false)
      setEditingNotification(null)
      resetForm()
      setSuccess('Notification updated successfully!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to update notification')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => pageNotificationsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['page-notifications'] })
      setSuccess('Notification deleted successfully!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to delete notification')
    },
  })

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      pageNotificationsApi.update(id, { is_active: is_active ? 1 : 0 }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['page-notifications'] })
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to toggle notification')
    },
  })

  const resetForm = () => {
    setFormData({
      page_route: 'all',
      message: '',
      type: 'info',
      is_active: true,
    })
    setError('')
  }

  const handleCreate = () => {
    setEditingNotification(null)
    resetForm()
    setShowModal(true)
  }

  const handleEdit = (notification: any) => {
    setEditingNotification(notification)
    setFormData({
      page_route: notification.page_route,
      message: notification.message,
      type: notification.type,
      is_active: notification.is_active === 1,
    })
    setShowModal(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.message.trim()) {
      setError('Please enter a message')
      return
    }

    const submitData = {
      ...formData,
      is_active: formData.is_active ? 1 : 0,
    }

    if (editingNotification) {
      updateMutation.mutate({ id: editingNotification.id, data: submitData })
    } else {
      createMutation.mutate(submitData)
    }
  }

  const handleDelete = (id: number) => {
    if (confirm('Are you sure you want to delete this notification?')) {
      deleteMutation.mutate(id)
    }
  }

  const handleToggleActive = (id: number, currentStatus: number) => {
    toggleActiveMutation.mutate({ id, is_active: currentStatus !== 1 })
  }

  const notifications = data?.data?.notifications || []

  const getPageLabel = (route: string) => {
    return PAGE_ROUTES.find((p) => p.value === route)?.label || route
  }

  const getTypeColor = (type: string) => {
    const typeObj = NOTIFICATION_TYPES.find((t) => t.value === type)
    return typeObj?.color || 'gray'
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Page Notifications</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage notifications that appear on specific pages
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="w-4 h-4 mr-2" />
          New Notification
        </Button>
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && !showModal && <Alert type="error" message={error} onClose={() => setError('')} />}

      {/* Notifications Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <ResponsiveTable>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Page
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Message
                </th>
                <th className="hide-mobile px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-priority="2">
                  Type
                </th>
                <th className="hide-mobile px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-priority="3">
                  Status
                </th>
                <th className="hide-mobile px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-priority="1">
                  Created By
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {notifications.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    No notifications yet. Create one to get started.
                  </td>
                </tr>
              ) : (
                notifications.map((notification: any) => (
                  <tr key={notification.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {getPageLabel(notification.page_route)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-md truncate">
                      {notification.message}
                    </td>
                    <td className="hide-mobile px-6 py-4 whitespace-nowrap" data-priority="2" data-label="Type">
                      <Badge status={getTypeColor(notification.type)}>
                        {notification.type}
                      </Badge>
                    </td>
                    <td className="hide-mobile px-6 py-4 whitespace-nowrap" data-priority="3" data-label="Status">
                      <Badge status={notification.is_active ? 'active' : 'inactive'}>
                        {notification.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="hide-mobile px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-priority="1" data-label="Created By">
                      {notification.first_name} {notification.last_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button
                        className="mobile-details-trigger text-blue-600 hover:text-blue-900"
                        title="View Hidden Details"
                      >
                        <Info className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleActive(notification.id, notification.is_active)}
                        className="text-gray-600 hover:text-gray-900"
                        title={notification.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {notification.is_active ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleEdit(notification)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(notification.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ResponsiveTable>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <Modal
          title={editingNotification ? 'Edit Notification' : 'Create Notification'}
          onClose={() => {
            setShowModal(false)
            setEditingNotification(null)
            resetForm()
          }}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <Alert type="error" message={error} onClose={() => setError('')} />}

            <div>
              <label className="label">Page *</label>
              <select
                className="select"
                value={formData.page_route}
                onChange={(e) => setFormData({ ...formData, page_route: e.target.value })}
                required
              >
                {PAGE_ROUTES.map((route) => (
                  <option key={route.value} value={route.value}>
                    {route.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Select the page where this notification will appear
              </p>
            </div>

            <div>
              <label className="label">Message *</label>
              <textarea
                className="textarea"
                rows={3}
                placeholder="Enter notification message..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="label">Type *</label>
              <select
                className="select"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                required
              >
                {NOTIFICATION_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                Active (visible to users)
              </label>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowModal(false)
                  setEditingNotification(null)
                  resetForm()
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={createMutation.isPending || updateMutation.isPending}
              >
                {editingNotification ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  )
}
